import type { GroupAccountItem } from './groups/group-account/types'
import type { InvoiceProcessingLevel } from '../../domain/invoice-processing-level.enum'
import type { PushType } from '../../domain/push-type.enum'

export interface Pagination {
  pageSize: number
  currentPage: number
}

export type PremiumAchPullStatus = 'initial_review' | 'on_approve' | 'submitted' | 'paid'

export type PremiumInvoiceStatus = 'open' | 'initial_review' | 'on_approve' | 'submitted' | 'paid'

export interface Location {
  id: string

  key: string
  name: string
  short_name: string
  location: string

  created_at: string
  updated_at: string
}

export interface SearchParams {
  query: string
  locationIds: string[]
  fundingTypes: Array<PushType | 'null'>
  pushTypes: Array<PushType | 'null'>
  networks: Array<string | 'null'>
  openAr: Array<'null' | 0 | 'negative' | 'positive'>
  currentAr: Array<'null' | 0 | 'negative' | 'positive'>
  schedule: Array<'null' | number>
  isActive: boolean[]
  flags: string[]

  nextPullDate: Array<'null' | string>
}

export interface HolidayOverride {
  date: string
}
export interface Holiday {
  date: string
  overrides?: HolidayOverride[]
}

export interface PremiumGroupInvoiceLine {
  id: string
  number: string

  amount: number
  balance_due: number
  total_credits: number

  description: string | null
  coverage_category: string | null
  billing_type: string | null

  created_at: string
  updated_at: string
}

export interface PremiumGroupInvoice {
  id: string
  number: string

  date: string
  billing_date: string

  division: string | null
  entity_id: string | null
  client_number: string | null

  status: PremiumInvoiceStatus

  lines: PremiumGroupInvoiceLine[]

  created_at: string
  updated_at: string
}

export interface PremiumGroup {
  id: string
  name: string
  number: string

  location: Location
  effective_date: string | null
  renewal_date: string | null
  termination_date: string | null
  run_out_date: string | null

  premium_routing_number: string | null
  premium_account_number: string | null
  pinnacle_routing_number: string | null
  pinnacle_account_number: string | null
  premium_funding_type: PushType | null
  premium_schedule_day: number | null
  premium_is_active: boolean
  premium_level_funded: boolean

  current_ar: number
  open_ar: number

  invoices: PremiumGroupInvoice[]
  release_type: string | null
  flags: string[]

  next_pull_date: string | null

  updated_by: string
  updated_at: string
  invoice_processing_level: InvoiceProcessingLevel
  accounts: GroupAccountItem[] | null
}

export interface PremiumAchPull {
  id: string
  name: string
  location: Location
  sending_date: string
  group_count: number
  invoice_sum: number | null
  status: PremiumAchPullStatus
  created_by: string
}

export type PremiumAchPullDetailed = PremiumAchPull & {
  groups: PremiumGroup[]
}

export interface PremiumAchPullInvoice {
  id: string
  number: string
  amount: number
  division: string
  billing_date: string
  lines: PremiumGroupInvoiceLine[]
  group_name: string
  group_number: string
  group_premium_funding_type: string
  group_premium_account_number: string | null
  group_premium_routing_number: string | null
}

export const PremiumAchPullStatusMapper: Record<
  PremiumAchPullStatus,
  { name: string; color: string; transition: PremiumAchPullStatus[] }
> = {
  initial_review: {
    name: 'On Review',
    color: 'cyan',
    transition: ['on_approve']
  },
  on_approve: {
    name: 'On Approve',
    color: 'orange',
    transition: ['initial_review', 'submitted']
  },
  submitted: {
    name: 'Submitted',
    color: 'green',
    transition: ['paid']
  },
  paid: {
    name: 'Paid',
    color: 'green',
    transition: []
  }
}

export const PremiumInvoiceStatusMapper: Record<
  PremiumInvoiceStatus,
  { name: string; color: string }
> = {
  open: {
    name: 'Open',
    color: 'blue'
  },
  initial_review: {
    name: 'On Review',
    color: 'cyan'
  },
  on_approve: {
    name: 'On Approve',
    color: 'orange'
  },
  submitted: {
    name: 'Submitted',
    color: 'green'
  },
  paid: {
    name: 'Paid',
    color: 'green'
  }
}

export type ListQuerySortField = keyof Omit<PremiumGroup, 'id'>
export type ListQuerySortOrder = 'asc' | 'desc'

export interface ListState {
  expandedRow: {
    id?: string
  }

  searchParams: SearchParams

  pagination: {
    pageSize: number
    currentPage: number
  }

  sort: {
    field?: ListQuerySortField
    order?: ListQuerySortOrder
  }

  refreshInterval?: number
}

export interface QueryFilter {
  value: string | number | boolean | null
  count: number
}
